import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { KeyRound, Check, AlertCircle } from 'lucide-react';
import { Button } from '../../../components/ui/Button';
import { Input } from '../../../components/ui/Input';
type Role = 'learner' | 'parent' | 'teacher';
interface RegisterPasswordStepProps {
  role: Role;
  password: string;
  passwordConfirm: string;
  validationError: string;
  isSubmitting: boolean;
  onPasswordChange: (value: string) => void;
  onPasswordConfirmChange: (value: string) => void;
  slideVariants: Record<string, unknown>;
}
export function RegisterPasswordStep({
  role,
  password,
  passwordConfirm,
  validationError,
  isSubmitting,
  onPasswordChange,
  onPasswordConfirmChange,
  slideVariants
}: RegisterPasswordStepProps) {
  return <motion.div key="step-password" custom={1} variants={slideVariants} initial="enter" animate="center" exit="exit" transition={{
    duration: 0.3
  }} className="space-y-5" data-id="element-394">
      <h2 className="text-xl font-black text-slate-900 text-center" data-id="element-395">
        Kies een wachtwoord
      </h2>

      <div data-id="element-396">
        <label className="block text-sm font-bold text-slate-600 mb-1.5" data-id="element-397">
          Wachtwoord
        </label>
        <div className="relative" data-id="element-398">
          <Input type="password" required autoFocus minLength={role === 'learner' ? 4 : 6} value={password} onChange={e => onPasswordChange(e.target.value)} icon={<KeyRound className="w-5 h-5" data-id="element-400" />} className="bg-slate-50 border-2 border-slate-200 rounded-2xl focus:bg-white focus:border-orange-400 text-lg font-medium tracking-wider py-4" placeholder={role === 'learner' ? '••••' : '••••••••'} data-id="element-399" />
        </div>
        <p className="text-xs text-slate-500 mt-2 ml-1" data-id="element-401">
          {role === 'learner' ? 'Minimaal 4 tekens voor leerlingen' : 'Minimaal 6 tekens'}
        </p>
        {/* Password strength hint */}
        {password.length > 0 && <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} className="mt-2 flex gap-1.5" data-id="element-402">
            {[1, 2, 3, 4].map(level => {
          const minLen = role === 'learner' ? 4 : 6;
          const strength = password.length >= minLen + 4 ? 4 : password.length >= minLen + 2 ? 3 : password.length >= minLen ? 2 : 1;
          const colors = ['bg-red-400', 'bg-orange-400', 'bg-amber-400', 'bg-emerald-400'];
          return <div key={level} className={`h-1.5 flex-1 rounded-full transition-colors ${level <= strength ? colors[strength - 1] : 'bg-slate-200'}`} data-id="element-403" />;
        })}
          </motion.div>}
      </div>

      <div data-id="element-404">
        <label className="block text-sm font-bold text-slate-600 mb-1.5" data-id="element-405">
          Herhaal wachtwoord
        </label>
        <div className="relative" data-id="element-406">
          <Input type="password" required minLength={role === 'learner' ? 4 : 6} value={passwordConfirm} onChange={e => onPasswordConfirmChange(e.target.value)} icon={<KeyRound className="w-5 h-5" data-id="element-408" />} className={`bg-slate-50 border-2 rounded-2xl focus:bg-white text-lg font-medium tracking-wider py-4 ${passwordConfirm && password !== passwordConfirm ? 'border-red-300 focus:border-red-400' : passwordConfirm && password === passwordConfirm ? 'border-emerald-400 focus:border-emerald-500' : 'border-slate-200 focus:border-orange-400'}`} placeholder="Herhaal je wachtwoord" data-id="element-407" />
          {passwordConfirm && password === passwordConfirm && <Check className="absolute right-4 top-1/2 -translate-y-1/2 w-5 h-5 text-emerald-500 z-10" data-id="element-409" />}
        </div>
        {passwordConfirm && password !== passwordConfirm && <p className="text-xs text-red-500 font-medium mt-1.5 flex items-center gap-1" data-id="element-410">
            <AlertCircle className="w-3 h-3" data-id="element-411" /> Wachtwoorden komen niet overeen
          </p>}
      </div>

      <AnimatePresence data-id="element-412">
        {validationError && <motion.div initial={{
        opacity: 0,
        height: 0
      }} animate={{
        opacity: 1,
        height: 'auto'
      }} exit={{
        opacity: 0,
        height: 0
      }} className="overflow-hidden" data-id="element-413">
            <div className="p-3 bg-red-50 text-red-700 text-sm font-medium rounded-2xl border border-red-200 flex items-start gap-2" data-id="element-414">
              <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" data-id="element-415" />
              <span data-id="element-416">{validationError}</span>
            </div>
          </motion.div>}
      </AnimatePresence>

      <Button type="submit" variant="primary" size="xl" className="w-full" disabled={isSubmitting || password.length < (role === 'learner' ? 4 : 6) || password !== passwordConfirm} data-id="element-417">
        {isSubmitting ? 'Even geduld...' : 'Account aanmaken 🎉'}
      </Button>
    </motion.div>;
}